// Phase A (2026-08-20) — Security Center (admin only).
// Tabs: Overview / Threats / Audit / Sessions / Settings.
import { useEffect, useMemo, useState } from "react";
import {
  Shield, AlertTriangle, ScrollText, Monitor, Settings2,
  RefreshCw, Search, CheckCircle2, XCircle, Trash2,
  Globe2, Users as UsersIcon, Lock, MailWarning,
  ChevronRight, Lock as LockIcon, Link2, Database,
  KeyRound, PlayCircle, Download, AlertOctagon,
  ShieldOff, ShieldAlert, FileWarning, Clock, Globe, Activity,
  Zap, LockKeyhole,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "overview",  label: "Overview",           icon: Shield },
  { key: "threats",   label: "Threats & Alerts",   icon: AlertTriangle },
  { key: "audit",     label: "Audit Log",          icon: ScrollText },
  { key: "sessions",  label: "Sessions & Devices", icon: Monitor },
  { key: "settings",  label: "Settings",           icon: Settings2 },
];

const SEV_COLOR = {
  critical: "bg-red-100 text-red-800 border-red-300",
  high:     "bg-orange-100 text-orange-800 border-orange-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-300",
  low:      "bg-slate-100 text-slate-700 border-slate-300",
};

function ScoreDial({ score }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 80 ? "#059669" : pct >= 60 ? "#d97706" : "#dc2626";
  return (
    <div className="relative w-40 h-40" data-testid="sec-score-dial">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="12" />
        <circle cx="60" cy="60" r="52" fill="none" stroke={color}
                strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${(pct * 2 * Math.PI * 52) / 100} 999`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-4xl font-semibold tabular-nums" style={{ color }}>{pct}</div>
        <div className="text-xs uppercase tracking-widest text-slate-500">Score</div>
      </div>
    </div>
  );
}

function LockdownBanner() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [otp, setOtp] = useState("");
  const refresh = async () => {
    try {
      const { data } = await api.get("/security/lockdown");
      setState(data);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const target = state?.active ? "lockdown_disable" : "lockdown_enable";
      // 1) get MFA step-up token
      const step = await api.post("/auth/step-up/mfa", { otp, action: target });
      // 2) call lockdown
      const path = state?.active ? "/security/lockdown/disable" : "/security/lockdown/enable";
      await api.post(path, { reason, step_up_token: step.data.step_up_token });
      toast.success(state?.active ? "Lockdown ended." : "System locked down.");
      setConfirmOpen(false); setOtp(""); setReason("");
      refresh();
    } catch (e) {
      toast.error("Lockdown toggle failed. Check your MFA code.");
    } finally { setBusy(false); }
  };

  if (!state) return null;
  return (
    <Card
      className={"p-5 border-2 " + (state.active
        ? "border-red-400 bg-red-50"
        : "border-slate-200")}
      data-testid="lockdown-card"
    >
      <div className="flex items-center gap-4">
        {state.active ? (
          <ShieldAlert className="w-10 h-10 text-red-600" />
        ) : (
          <ShieldOff className="w-10 h-10 text-slate-400" />
        )}
        <div className="flex-1">
          <div className="text-sm uppercase tracking-widest text-slate-500">
            Emergency Lockdown
          </div>
          <div className="text-xl font-semibold text-slate-800 mt-1"
               data-testid="lockdown-status-label">
            {state.active
              ? "SYSTEM IS IN LOCKDOWN"
              : "Normal operation"}
          </div>
          {state.active && state.current?.started_by_email && (
            <div className="text-xs text-red-800 mt-1">
              Started by <b>{state.current.started_by_email}</b>
              {" "}at {new Date(state.current.started_at).toLocaleString()}
              {state.current.reason ? ` — ${state.current.reason}` : ""}
            </div>
          )}
        </div>
        {!confirmOpen && (
          <Button
            variant={state.active ? "outline" : "destructive"}
            onClick={() => setConfirmOpen(true)}
            data-testid="lockdown-open-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            {state.active ? "End Lockdown" : "Start Emergency Lockdown"}
          </Button>
        )}
      </div>
      {confirmOpen && (
        <div className="mt-4 border-t border-slate-200 pt-4 space-y-3">
          {!state.active && (
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Reason (recorded in audit log)
              </label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="e.g. Suspected credential theft — pausing all non-admin traffic"
                     data-testid="lockdown-reason" />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">
              MFA code (6 digits)
            </label>
            <Input value={otp} inputMode="numeric" maxLength={6}
                   onChange={(e) => setOtp(e.target.value.replace(/\D/g,"").slice(0,6))}
                   placeholder="123456" className="tracking-widest"
                   data-testid="lockdown-otp" />
          </div>
          <div className="flex gap-2">
            <Button variant="destructive" disabled={busy || otp.length !== 6}
                    onClick={toggle} data-testid="lockdown-confirm-btn">
              {busy ? "Working…"
                : state.active ? "End Lockdown" : "Confirm Lockdown"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}
                    data-testid="lockdown-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/stats/overview");
      setStats(data);
    } catch (e) {
      toast.error("Failed to load overview.");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  if (loading || !stats) return <div className="p-8 text-slate-500">Loading…</div>;
  const sev = stats.threats_24h || {};
  return (
    <div className="space-y-6" data-testid="sec-overview-tab">
      <LockdownBanner />
      {!stats.smtp_configured && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-300
                        text-amber-900 rounded-md px-4 py-3 text-sm"
             data-testid="smtp-warning">
          <MailWarning className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>SMTP not configured</b> — email alerts are disabled (in-app only).
            Set <code>SMTP_HOST</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code> and
            <code>SMTP_FROM</code> environment variables to enable email delivery.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 flex items-center gap-4" data-testid="sec-card-score">
          <ScoreDial score={stats.score} />
          <div>
            <div className="text-sm text-slate-500 uppercase tracking-widest">
              Security Score
            </div>
            <div className="text-slate-700 mt-1 text-sm max-w-[13rem]">
              Composite based on MFA adoption, open threats, and recent activity.
            </div>
          </div>
        </Card>
        <Card className="p-5" data-testid="sec-card-mfa">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
            <Lock className="w-3 h-3" /> MFA Adoption
          </div>
          <div className="text-4xl font-semibold tabular-nums mt-3">
            {stats.mfa_adoption_pct}%
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {stats.mfa_users} / {stats.total_users} users enrolled
          </div>
        </Card>
        <Card className="p-5" data-testid="sec-card-threats">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
            <AlertTriangle className="w-3 h-3" /> Threats (24h)
          </div>
          <div className="text-4xl font-semibold tabular-nums mt-3">
            {(sev.critical || 0) + (sev.high || 0) + (sev.medium || 0) + (sev.low || 0)}
          </div>
          <div className="text-xs mt-2 flex gap-2 flex-wrap">
            {["critical","high","medium","low"].map(s => (
              <span key={s} className={"px-2 py-0.5 rounded border " + SEV_COLOR[s]}>
                {sev[s] || 0} {s}
              </span>
            ))}
          </div>
        </Card>
        <Card className="p-5" data-testid="sec-card-sessions">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
            <UsersIcon className="w-3 h-3" /> Active Sessions
          </div>
          <div className="text-4xl font-semibold tabular-nums mt-3">
            {stats.active_sessions}
          </div>
          <div className="text-sm text-slate-500 mt-1">Trusted devices online</div>
        </Card>
      </div>

      <Card className="p-5" data-testid="sec-card-geo">
        <div className="flex items-center gap-2 mb-4">
          <Globe2 className="w-4 h-4 text-slate-500" />
          <h3 className="text-slate-700 text-sm uppercase tracking-widest">
            Logins by Country — last 7 days
          </h3>
          <Button size="sm" variant="ghost" onClick={refresh}
                  data-testid="sec-refresh-overview">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
        {stats.countries_7d.length === 0 ? (
          <div className="text-slate-500 text-sm">
            No country data yet — login events without geo enrichment (localhost)
            do not appear here.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {stats.countries_7d.map(c => (
              <div key={c.country_code}
                   className="border border-slate-200 rounded-md px-3 py-2 flex flex-col items-start"
                   data-testid={`geo-${c.country_code}`}>
                <div className="text-xs text-slate-500 tracking-widest">
                  {c.country_code}
                </div>
                <div className="text-xl font-semibold tabular-nums">{c.count}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Phase B — Encryption + Audit chain cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5" data-testid="sec-card-encryption">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
            <LockIcon className="w-3 h-3" /> PII Encryption
          </div>
          {stats.encryption ? (
            <>
              <div className="mt-3 text-3xl font-semibold tabular-nums flex items-baseline gap-2">
                {stats.encryption.users_email_encrypted}
                <span className="text-slate-400 text-lg">/ {stats.encryption.users_email_total}</span>
                {stats.encryption.users_email_encrypted === stats.encryption.users_email_total ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertOctagon className="w-5 h-5 text-amber-500" />
                )}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Users' email at rest (envelope + blind index).
              </div>
              <div className="mt-3 text-xs text-slate-400">
                Key: <code className="text-slate-600">{stats.encryption.primary_kid}</code>
                {stats.encryption.key_ids.length > 1 &&
                  <span> · {stats.encryption.key_ids.length} keys in ring</span>}
              </div>
            </>
          ) : (
            <div className="mt-3 text-slate-400 text-sm">Not available.</div>
          )}
        </Card>
        <Card className="p-5" data-testid="sec-card-chain">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
            <Link2 className="w-3 h-3" /> Audit Chain
          </div>
          {stats.audit_chain && Object.keys(stats.audit_chain).length ? (
            <>
              <div className="mt-3 flex items-baseline gap-2">
                {stats.audit_chain.ok
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  : <AlertOctagon className="w-5 h-5 text-red-600" />}
                <div className="text-2xl font-semibold tabular-nums">
                  {stats.audit_chain.ok ? "Verified" : "BROKEN"}
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {stats.audit_chain.verified_rows} row(s) checked in last verify.
              </div>
              {stats.audit_chain.last_verified_at && (
                <div className="text-xs text-slate-400 mt-1">
                  Last check: {new Date(stats.audit_chain.last_verified_at).toLocaleString()}
                </div>
              )}
              {!stats.audit_chain.ok && (
                <div className="text-xs text-red-700 mt-2 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {stats.audit_chain.reason} @ seq #{stats.audit_chain.first_broken_seq}
                </div>
              )}
            </>
          ) : (
            <div className="mt-3 text-slate-400 text-sm">Not verified yet.</div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ThreatsTab() {
  const [alerts, setAlerts] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/alerts", { params: { status, limit: 200 } });
      setAlerts(data.alerts || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status]);
  const ack = async (id) => {
    await api.post(`/security/alerts/${id}/ack`, { note: "" });
    toast.success("Alert acknowledged.");
    refresh();
  };
  const dismiss = async (id) => {
    await api.post(`/security/alerts/${id}/dismiss`, {});
    toast.info("Alert dismissed.");
    refresh();
  };
  return (
    <div className="space-y-4" data-testid="sec-threats-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["open","acknowledged","dismissed","all"].map(s => (
          <Button key={s} size="sm"
                  variant={status === s ? "default" : "outline"}
                  onClick={() => setStatus(s)}
                  data-testid={`threats-filter-${s}`}>
            {s[0].toUpperCase() + s.slice(1)}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={refresh} data-testid="threats-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="p-8 text-slate-500">Loading…</div>
      ) : alerts.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="threats-empty">
          <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
          No {status} alerts. Everything looks calm.
        </Card>
      ) : (
        <div className="space-y-2">
          {alerts.map(a => (
            <Card key={a.id} className="p-4" data-testid={`alert-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className={"px-2 py-0.5 rounded border text-[11px] uppercase " +
                                SEV_COLOR[a.severity]}>
                  {a.severity}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800">{a.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{a.description}</div>
                  <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-x-3">
                    <span>{new Date(a.created_at).toLocaleString()}</span>
                    {a.ip ? <span>IP {a.ip}</span> : null}
                    {a.country ? <span>Country {a.country}</span> : null}
                    <span>Rule: {a.rule_id}</span>
                  </div>
                </div>
                {a.status === "open" && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => ack(a.id)}
                            data-testid={`alert-ack-${a.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Ack
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => dismiss(a.id)}
                            data-testid={`alert-dismiss-${a.id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  </div>
                )}
                {a.status !== "open" && (
                  <Badge variant="outline" className="capitalize">{a.status}</Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 25;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/audit-log", {
        params: { q: q || undefined, action: action || undefined, page, page_size: pageSize },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [page, action]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const download = () => {
    const csv = ["timestamp,actor,action,resource_type,resource_id,ip,country,status"];
    rows.forEach(r => {
      csv.push([r.ts, r.actor_email || "", r.action, r.resource_type || "",
                r.resource_id || "", r.ip || "", r.country || "",
                r.http_status || ""].map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-4" data-testid="sec-audit-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3 h-3 absolute top-2.5 left-2 text-slate-400" />
          <Input placeholder="Search actor / resource id / IP"
                 className="pl-7 w-72" value={q}
                 onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); refresh(); }}}
                 onChange={e => setQ(e.target.value)}
                 data-testid="audit-search-input" />
        </div>
        <Input placeholder="Action (e.g. login_success)"
               value={action}
               onChange={e => setAction(e.target.value)}
               className="w-56"
               data-testid="audit-action-filter" />
        <Button size="sm" onClick={() => { setPage(1); refresh(); }}
                data-testid="audit-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Filter
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={download}
                data-testid="audit-export-csv">Export CSV (this page)</Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Resource</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (<tr><td colSpan={6} className="p-6 text-slate-500 text-center">Loading…</td></tr>)}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-slate-500 text-center">No entries.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                  {new Date(r.ts).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                  {r.actor_email || <span className="text-slate-400 italic">system</span>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.action}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">
                  {r.resource_type}{r.resource_id ? ` · ${r.resource_id.slice(0,8)}…` : ""}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.ip || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.http_status || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="flex items-center justify-between text-sm text-slate-500">
        <div>Total: {total.toLocaleString()} · Page {page}/{totalPages}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="audit-prev">Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="audit-next">Next</Button>
        </div>
      </div>
    </div>
  );
}

function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/sessions");
      setSessions(data.sessions || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  const revoke = async (deviceId) => {
    if (!confirm("Force-revoke this session? User will be signed out on that device.")) return;
    await api.delete(`/security/sessions/${deviceId}`);
    toast.success("Session revoked.");
    refresh();
  };
  return (
    <div className="space-y-4" data-testid="sec-sessions-tab">
      <div className="flex items-center justify-between">
        <div className="text-slate-500 text-sm">
          {sessions.length.toLocaleString()} active trusted device(s) across all users.
        </div>
        <Button size="sm" variant="ghost" onClick={refresh} data-testid="sec-sessions-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">IP / Country</th>
              <th className="text-left px-3 py-2">Browser</th>
              <th className="text-left px-3 py-2">First seen</th>
              <th className="text-left px-3 py-2">Last seen</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (<tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>)}
            {!loading && sessions.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">No active sessions.</td></tr>
            )}
            {sessions.map(s => (
              <tr key={s.device_id} data-testid={`session-row-${s.device_id}`}>
                <td className="px-3 py-2 text-slate-700 text-xs">{s.user_id}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {s.ip} {s.country ? `· ${s.country}` : ""}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 max-w-xs truncate"
                    title={s.ua}>{s.ua}</td>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                  {new Date(s.first_seen).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                  {new Date(s.last_seen).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline"
                          onClick={() => revoke(s.device_id)}
                          data-testid={`session-revoke-${s.device_id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function EncryptionBackupCard() {
  const [enc, setEnc] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [toolsStatus, setToolsStatus] = useState(null);
  const refresh = async () => {
    const [encR, jobsR, toolsR] = await Promise.all([
      api.get("/security/encryption/status"),
      api.get("/security/backups"),
      api.get("/security/settings/backup-tools-status").catch(() => ({ data: null })),
    ]);
    setEnc(encR.data);
    setJobs(jobsR.data.jobs || []);
    setToolsStatus(toolsR.data);
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 6000); return () => clearInterval(t); }, []);
  const runVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/security/audit-log/verify/latest");
      setVerifyResult(data);
      toast[data.ok ? "success" : "error"](
        data.ok ? `Chain verified — ${data.verified_rows} rows` :
                   `BROKEN at seq #${data.first_broken_seq}: ${data.reason}`
      );
    } catch (e) {
      toast.error("Verify failed.");
    } finally { setBusy(false); }
  };
  const runBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/security/backups/run");
      toast.success(`Backup ${data.id.slice(0,8)}… queued.`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "mongodump_not_installed") {
        toast.error(`Backups disabled: ${detail.hint || detail.message}`);
      } else if (typeof detail === "string") {
        toast.error(`Backup run failed: ${detail}`);
      } else {
        toast.error("Backup run failed. See Security Center banner for details.");
      }
    } finally { setBusy(false); }
  };
  const toolsMissing = toolsStatus && !toolsStatus.ok;
  return (
    <Card className="p-5 space-y-5" data-testid="sec-enc-backup-card">
      <div className="flex items-center gap-2">
        <LockIcon className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Encryption & Backup</h3>
      </div>

      {toolsMissing && (
        <div className="border border-red-300 bg-red-50 text-red-800 rounded px-3 py-2 text-sm flex items-start gap-2"
             data-testid="backup-tools-missing-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">mongodump missing — backups disabled.</div>
            <div className="text-xs mt-1">
              {toolsStatus.hint || "Install mongodb-database-tools in the container."}
            </div>
          </div>
        </div>
      )}

      {/* Encryption status */}
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-2">
          <KeyRound className="w-3 h-3" /> Encryption Keys
        </div>
        {enc ? (
          <>
            <div className="text-sm text-slate-700 mb-2">
              Primary key: <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                {enc.primary_kid}
              </code>
              {enc.key_ids.length > 1 &&
                <span className="text-slate-500 ml-2">
                  · {enc.key_ids.length} keys in rotation ring
                </span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mt-2">
              {Object.entries(enc.collections).map(([coll, fields]) =>
                Object.entries(fields).filter(([_, s]) => s.total > 0).map(([f, s]) => (
                  <div key={`${coll}.${f}`}
                       className={"border rounded px-2 py-1 flex items-center justify-between " +
                                    (s.encrypted === s.total
                                      ? "border-emerald-200 bg-emerald-50"
                                      : "border-amber-200 bg-amber-50")}
                       data-testid={`enc-${coll}-${f}`}>
                    <span className="text-slate-600">{coll}.{f}</span>
                    <span className="tabular-nums text-slate-800">
                      {s.encrypted}/{s.total}
                    </span>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="text-slate-500 text-sm">Loading…</div>
        )}
      </div>

      {/* Audit chain */}
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-2">
          <Link2 className="w-3 h-3" /> Audit Chain Integrity
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button size="sm" variant="outline" onClick={runVerify} disabled={busy}
                  data-testid="verify-audit-chain-btn">
            <PlayCircle className="w-4 h-4 mr-2" />
            {busy ? "Verifying…" : "Verify audit chain now"}
          </Button>
          {verifyResult && (
            <span className={"text-sm " + (verifyResult.ok ? "text-emerald-700" : "text-red-700")}>
              {verifyResult.ok
                ? `✓ ${verifyResult.verified_rows} rows verified`
                : `✗ Broken at #${verifyResult.first_broken_seq}: ${verifyResult.reason}`}
            </span>
          )}
        </div>
      </div>

      {/* Backups */}
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-2">
          <Database className="w-3 h-3" /> Encrypted Backups
        </div>
        <div className="flex gap-2 items-center mb-3">
          <Button size="sm" onClick={runBackup} disabled={busy}
                  data-testid="run-backup-btn">
            <Database className="w-4 h-4 mr-2" />
            {busy ? "Working…" : "Run encrypted backup now"}
          </Button>
          <Button size="sm" variant="ghost" onClick={refresh}
                  data-testid="backups-refresh">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
        <div className="border border-slate-200 rounded overflow-x-auto"
             data-testid="backups-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
              <tr>
                <th className="text-left px-3 py-2">Requested</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Size</th>
                <th className="text-left px-3 py-2">SHA-256</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-slate-500 text-center">
                  No backups yet. Click "Run encrypted backup now" to create one.
                </td></tr>
              )}
              {jobs.slice(0, 15).map(j => (
                <tr key={j.id} data-testid={`backup-row-${j.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {new Date(j.requested_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{j.requested_by}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={
                      "px-2 py-0.5 rounded uppercase text-[10px] tracking-widest " +
                      ({done:"bg-emerald-100 text-emerald-800",
                         running:"bg-blue-100 text-blue-800 animate-pulse",
                         queued:"bg-slate-100 text-slate-700",
                         failed:"bg-red-100 text-red-800",
                         pruned:"bg-slate-100 text-slate-500"}[j.status] || "bg-slate-100")
                    }>{j.status}</span>
                    {j.step && j.status !== "done" && (
                      <span className="text-slate-400 ml-2">{j.step}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 tabular-nums">
                    {j.size ? (j.size/1024/1024).toFixed(2) + " MB" : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-400"
                      title={j.sha256 || ""}
                      onClick={() => {
                        if (j.sha256) {
                          navigator.clipboard.writeText(j.sha256);
                          toast.success("SHA-256 copied");
                        }
                      }}
                      style={{ cursor: j.sha256 ? "copy" : "default" }}
                      data-testid={`backup-sha-${j.id}`}>
                    {j.sha256 ? j.sha256.slice(0, 12) + "…" : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {j.status === "done" && j.download_token && (
                      <Button size="sm" variant="outline"
                              onClick={() => {
                                const API = (window && window.__API__)
                                  || (process.env.REACT_APP_BACKEND_URL || "");
                                const url = `${API}/api/security/backups/${j.id}/download?token=${encodeURIComponent(j.download_token)}`;
                                window.open(url, "_blank");
                              }}
                              data-testid={`backup-download-${j.id}`}>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    )}
                    {j.status === "failed" && (
                      <span className="text-xs text-red-700" title={j.error}>failed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

function RateLimitCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    const { data } = await api.get("/security/rate-limit/status");
    setStatus(data);
  };
  useEffect(() => { refresh(); }, []);
  const toggleShadow = async () => {
    setBusy(true);
    try {
      await api.post("/security/rate-limit/shadow",
        { shadow_mode: !status.shadow_mode });
      toast.success(
        !status.shadow_mode
          ? "Shadow mode ON — breaches logged, not blocked."
          : "Shadow mode OFF — breaches now HARD BLOCK (429).");
      refresh();
    } catch (_e) { toast.error("Toggle failed."); }
    finally { setBusy(false); }
  };
  if (!status) return null;
  return (
    <Card className="p-5 space-y-4" data-testid="sec-ratelimit-card">
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Rate Limiting</h3>
        <span className={"px-2 py-0.5 text-xs uppercase tracking-widest rounded " +
          (status.shadow_mode
            ? "bg-amber-100 text-amber-800"
            : "bg-emerald-100 text-emerald-800")}
              data-testid="ratelimit-mode-label">
          {status.shadow_mode ? "Shadow — log only" : "Enforcing"}
        </span>
      </div>
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
        <b>Note:</b> Rate limits are enforced <b>per pod</b> using in-memory
        counters. For distributed enforcement across replicas, set
        <code className="mx-1">SECURITY_RATE_LIMIT_REDIS_URL</code> in production.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {status.tiers && Object.entries(status.tiers).map(([name, t]) => (
          <div key={name}
               className="border border-slate-200 rounded px-3 py-2 flex items-center justify-between"
               data-testid={`ratelimit-tier-${name}`}>
            <span className="text-slate-600 uppercase tracking-widest">{name}</span>
            <span className="font-mono text-slate-800">{t.limit}</span>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={toggleShadow} disabled={busy}
              data-testid="ratelimit-toggle-shadow">
        {status.shadow_mode
          ? "Flip to ENFORCE (hard block)"
          : "Flip back to SHADOW (log only)"}
      </Button>
    </Card>
  );
}

function IpPolicyCard() {
  const [admins, setAdmins] = useState([]);
  const [busy, setBusy] = useState(null);
  const [drafts, setDrafts] = useState({});
  const refresh = async () => {
    const { data } = await api.get("/security/ip-policy");
    setAdmins(data.admins || []);
    setDrafts(Object.fromEntries(
      (data.admins || []).map(a => [a.id, (a.allowed_cidrs || []).join(", ")])));
  };
  useEffect(() => { refresh(); }, []);
  const save = async (id) => {
    setBusy(id);
    try {
      const cidrs = (drafts[id] || "").split(",").map(x => x.trim()).filter(Boolean);
      await api.patch("/security/ip-policy", { user_id: id, cidrs });
      toast.success("IP allow-list saved.");
      refresh();
    } catch (_e) { toast.error("Save failed."); }
    finally { setBusy(null); }
  };
  const addMyIp = async (id) => {
    try {
      const { data } = await axios.get("https://api.ipify.org?format=json");
      const cur = drafts[id] || "";
      const merged = cur ? `${cur}, ${data.ip}/32` : `${data.ip}/32`;
      setDrafts({ ...drafts, [id]: merged });
    } catch (_e) {
      toast.error("Could not detect your IP.");
    }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="sec-ippolicy-card">
      <div className="flex items-center gap-2">
        <Globe className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Admin IP Allow-List</h3>
      </div>
      <p className="text-xs text-slate-500">
        Empty list = allow all. Non-admin users are never affected by this
        setting.
      </p>
      {admins.map(a => (
        <div key={a.id}
             className="border border-slate-200 rounded p-3 space-y-2"
             data-testid={`ippolicy-row-${a.id}`}>
          <div className="text-sm font-medium text-slate-700">{a.email}</div>
          <Input value={drafts[a.id] ?? ""}
                 onChange={(e) => setDrafts({ ...drafts, [a.id]: e.target.value })}
                 placeholder="e.g. 10.0.0.0/24, 192.168.1.100/32"
                 data-testid={`ippolicy-input-${a.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save(a.id)}
                    disabled={busy === a.id}
                    data-testid={`ippolicy-save-${a.id}`}>
              {busy === a.id ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ))}
    </Card>
  );
}

function UploadSecurityCard() {
  const [s, setS] = useState(null);
  const refresh = async () => {
    const { data } = await api.get("/security/upload/status");
    setS(data);
  };
  useEffect(() => { refresh(); }, []);
  if (!s) return null;
  const clam = s.scanner.scanner === "clamd" && s.scanner.ok;
  return (
    <Card className="p-5 space-y-3" data-testid="sec-upload-card">
      <div className="flex items-center gap-2">
        <FileWarning className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Upload Security</h3>
      </div>
      <div className={"text-xs px-3 py-2 rounded border " +
        (clam
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-amber-50 border-amber-300 text-amber-900")}
           data-testid="upload-scanner-status">
        {clam
          ? (<><CheckCircle2 className="w-3 h-3 inline mr-1" />ClamAV daemon reachable — full AV enabled.</>)
          : (<><AlertOctagon className="w-3 h-3 inline mr-1" />
              <b>ClamAV not configured.</b> Falling back to EICAR-signature
              matcher (blocks the standard AV test string). Set
              <code className="mx-1">SECURITY_CLAMAV_HOST</code> to a running
              clamd socket to enable full anti-virus.</>)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <div className="border border-slate-200 rounded px-3 py-2">
          <span className="text-slate-500 uppercase tracking-widest">Max size</span>
          <div className="text-base font-semibold">{s.max_mb} MB</div>
        </div>
        <div className="border border-slate-200 rounded px-3 py-2">
          <span className="text-slate-500 uppercase tracking-widest">Allowed extensions</span>
          <div className="text-slate-700 mt-1 font-mono text-xs">
            {(s.allowed_exts || []).join(", ")}
          </div>
        </div>
      </div>
    </Card>
  );
}

function BackupScheduleCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const refresh = async () => {
    const { data } = await api.get("/security/backups/schedule");
    setS(data);
  };
  useEffect(() => { refresh(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    setSaving(true);
    try {
      await api.patch("/security/backups/schedule", patch);
      toast.success("Backup schedule updated.");
      refresh();
    } catch (_e) { toast.error("Update failed."); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="sec-backup-schedule-card">
      <div className="flex items-center gap-2">
        <Clock className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Scheduled Backups</h3>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={s.enabled}
                 onChange={(e) => save({ enabled: e.target.checked })}
                 data-testid="backup-sched-enabled" />
          <span>Enabled</span>
        </label>
        <span className="text-slate-400">·</span>
        <span className="text-slate-500 text-xs">
          Cron (UTC):
        </span>
        <Input value={s.cron} onChange={(e) => setS({ ...s, cron: e.target.value })}
               onBlur={() => save({ cron: s.cron })}
               className="w-40 font-mono"
               data-testid="backup-sched-cron" />
      </div>
      <div className="text-xs text-slate-500">
        Next run:{" "}
        <span className="font-mono text-slate-700">
          {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}
        </span>
      </div>
      <p className="text-xs text-slate-400">
        Scheduled runs create encrypted backups with the same rotation rules
        (30 daily + 12 monthly). Failures raise a CRITICAL alert.
      </p>
    </Card>
  );
}

function GuardModesCard() {
  const [csrfMode, setCsrfMode] = useState("warn");
  const [nosqlMode, setNosqlMode] = useState("warn");
  const [busy, setBusy] = useState(false);
  const [headers, setHeaders] = useState(null);
  const refresh = async () => {
    try {
      const s = await api.get("/security/settings");
      if (s.data.csrf_enforce) setCsrfMode(s.data.csrf_enforce);
      if (s.data.nosql_guard_enforce) setNosqlMode(s.data.nosql_guard_enforce);
      const h = await api.get("/security/headers");
      setHeaders(h.data.headers);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);
  const apply = async (kind, mode) => {
    setBusy(true);
    try {
      await api.post(`/security/${kind}/mode`, { mode });
      if (kind === "csrf") setCsrfMode(mode); else setNosqlMode(mode);
      toast.success(`${kind.toUpperCase()} mode set to ${mode}.`);
    } catch (_e) { toast.error("Update failed."); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="sec-guardmodes-card">
      <div className="flex items-center gap-2">
        <LockKeyhole className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Request Guards & Response Headers</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
            CSRF (double-submit)
          </div>
          <div className="flex gap-1">
            {["warn","enforce","off"].map(m => (
              <Button key={m} size="sm"
                      variant={csrfMode === m ? "default" : "outline"}
                      disabled={busy}
                      onClick={() => apply("csrf", m)}
                      data-testid={`csrf-mode-${m}`}>
                {m}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
            NoSQL Injection Guard
          </div>
          <div className="flex gap-1">
            {["warn","enforce","off"].map(m => (
              <Button key={m} size="sm"
                      variant={nosqlMode === m ? "default" : "outline"}
                      disabled={busy}
                      onClick={() => apply("nosql-guard", m)}
                      data-testid={`nosql-mode-${m}`}>
                {m}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {headers && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600">
            Response headers (read-only for auditor screenshots)
          </summary>
          <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-3 font-mono">
            {Object.entries(headers).map(([k, v]) => (
              <div key={k} className="mb-1">
                <b>{k}:</b> <span className="text-slate-600">{String(v).slice(0, 220)}
                {String(v).length > 220 ? "…" : ""}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const refresh = async () => {
    const { data } = await api.get("/security/settings");
    setS(data);
  };
  useEffect(() => { refresh(); }, []);
  if (!s) return <div className="p-8 text-slate-500">Loading…</div>;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/security/settings", {
        high_value_je_usd: Number(s.high_value_je_usd) || 0,
        mfa_grace_days: Number(s.mfa_grace_days) || 7,
        allowed_countries: (s.allowed_countries || []).map(x => x.toUpperCase()),
        blocked_countries: (s.blocked_countries || []).map(x => x.toUpperCase()),
        alert_email_recipients: s.alert_email_recipients || [],
      });
      toast.success("Security settings updated.");
    } catch (e) {
      toast.error("Save failed.");
    } finally { setSaving(false); }
  };
  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  return (
    <div className="max-w-2xl space-y-4" data-testid="sec-settings-tab">
      <Card className="p-5 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            High-value JE threshold (USD)
          </label>
          <Input type="number" value={s.high_value_je_usd}
                 onChange={e => set("high_value_je_usd", e.target.value)}
                 data-testid="settings-hvje" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            MFA grace period (days)
          </label>
          <Input type="number" min={0} max={90} value={s.mfa_grace_days}
                 onChange={e => set("mfa_grace_days", e.target.value)}
                 data-testid="settings-mfa-grace" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            Allowed countries (ISO codes, comma-separated; empty = all allowed)
          </label>
          <Input value={(s.allowed_countries || []).join(",")}
                 onChange={e => set("allowed_countries",
                   e.target.value.split(",").map(x => x.trim()).filter(Boolean))}
                 data-testid="settings-allow" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            Blocked countries (ISO codes, comma-separated)
          </label>
          <Input value={(s.blocked_countries || []).join(",")}
                 onChange={e => set("blocked_countries",
                   e.target.value.split(",").map(x => x.trim()).filter(Boolean))}
                 data-testid="settings-block" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            Alert email recipients (comma-separated)
          </label>
          <Input value={(s.alert_email_recipients || []).join(",")}
                 onChange={e => set("alert_email_recipients",
                   e.target.value.split(",").map(x => x.trim()).filter(Boolean))}
                 data-testid="settings-recipients" />
        </div>
        <div className="pt-2">
          <Button onClick={save} disabled={saving} data-testid="settings-save">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </Card>
      <EncryptionBackupCard />
      <BackupScheduleCard />
      <RateLimitCard />
      <IpPolicyCard />
      <UploadSecurityCard />
      <GuardModesCard />
    </div>
  );
}

export default function SecurityCenterPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const Tab = useMemo(() => ({
    overview: OverviewTab, threats: ThreatsTab, audit: AuditTab,
    sessions: SessionsTab, settings: SettingsTab,
  }[tab]), [tab]);
  const isAdmin = user?.role === "system_admin" || user?.full_access === true;
  if (!isAdmin) {
    return <div className="p-8 text-slate-500">Admin only.</div>;
  }
  return (
    <div className="space-y-6" data-testid="security-center-page">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-slate-500" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Security Center</h1>
          <p className="text-sm text-slate-500">
            Threats, audit trail, sessions, and policy — all in one place.
          </p>
        </div>
      </div>
      <div className="border-b border-slate-200 flex flex-wrap gap-1"
           data-testid="security-center-tabs">
        {TABS.map(t => {
          const Ic = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={"px-4 py-2 text-sm flex items-center gap-2 border-b-2 " +
                               (active ? "border-slate-800 text-slate-800 font-medium"
                                       : "border-transparent text-slate-500 hover:text-slate-800")}
                    data-testid={`sec-tab-${t.key}`}>
              <Ic className="w-4 h-4" /> {t.label}
              {active && <ChevronRight className="w-3 h-3 opacity-0" />}
            </button>
          );
        })}
      </div>
      <div><Tab /></div>
    </div>
  );
}
